import { ReactNode, useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import { cn } from '@/lib/misc/Utils'

interface NavDrawerProps {
  isOpen: boolean
  onClose: () => void
  children: ReactNode
  /** Panel classes (background, width). Defaults to the light rail treatment. */
  className?: string
  /** Distance the backdrop is inset from the top, to sit below a fixed bar. */
  topOffsetClass?: string
  label?: string
}

/**
 * Mobile navigation drawer. Owns the backdrop, body scroll lock, Escape
 * handling and close-on-navigation so the toolbar and sidebar don't each
 * reimplement them.
 */
const NavDrawer = ({
  isOpen,
  onClose,
  children,
  className,
  topOffsetClass,
  label = 'Navigation',
}: NavDrawerProps) => {
  const location = useLocation()

  // Close whenever the route changes, so tapping a destination dismisses it.
  useEffect(() => {
    if (isOpen) onClose()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  useEffect(() => {
    if (!isOpen) return

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <>
      <div
        className={cn(
          'fixed inset-0 z-40 bg-text-darker/25 backdrop-blur-[2px] md:hidden',
          topOffsetClass
        )}
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={label}
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-[280px] max-w-[85vw] overflow-y-auto bg-canvas shadow-popover md:hidden',
          topOffsetClass,
          className
        )}
      >
        {children}
      </div>
    </>
  )
}

export default NavDrawer
